import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #isCleaningOrphans = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#cleanupOrphanedAddons();
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#cleanupOrphanedAddons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Cleans up orphaned saree add-on services if the parent saree is no longer in the cart.
   */
  #cleanupOrphanedAddons() {
    if (this.#isCleaningOrphans) return;

    const allRows = Array.from(this.querySelectorAll('tbody tr[data-key]'));
    if (!allRows.length) return;

    const norm = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const nonAddonRows = allRows.filter((r) => r.dataset.isAddon !== 'true');
    const addonRows = allRows.filter((r) => r.dataset.isAddon === 'true');

    if (!addonRows.length) return;

    // Case 1: If there are NO sarees/main products in the cart at all, all add-ons are orphans
    if (nonAddonRows.length === 0) {
      this.#isCleaningOrphans = true;
      const updates = {};
      addonRows.forEach((r) => {
        if (r.dataset.key) updates[r.dataset.key] = 0;
      });

      this.updateQuantity({
        line: 1,
        quantity: 0,
        action: 'clear',
        multiUpdates: updates,
      });
      setTimeout(() => { this.#isCleaningOrphans = false; }, 1000);
      return;
    }

    // Case 2: Sarees exist, verify each add-on belongs to an active saree
    const parentBundleIds = new Set(nonAddonRows.map((r) => r.dataset.bundleId).filter(Boolean));
    const parentTitles = nonAddonRows.flatMap((r) => [
      norm(r.dataset.productTitle),
      norm(r.dataset.itemTitle),
    ]).filter(Boolean);

    const orphanedRows = addonRows.filter((addon) => {
      const addonBundle = addon.dataset.bundleId;
      const forSaree = norm(addon.dataset.forSaree);

      if (addonBundle && parentBundleIds.has(addonBundle)) return false;
      if (forSaree && parentTitles.some((t) => t === forSaree || t.includes(forSaree) || forSaree.includes(t))) {
        return false;
      }
      return true;
    });

    if (orphanedRows.length > 0) {
      this.#isCleaningOrphans = true;
      const updates = {};
      orphanedRows.forEach((r) => {
        if (r.dataset.key) updates[r.dataset.key] = 0;
      });

      this.updateQuantity({
        line: 1,
        quantity: 0,
        action: 'clear',
        multiUpdates: updates,
      });
      setTimeout(() => { this.#isCleaningOrphans = false; }, 1000);
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line, event);
    }

    const lineItemRow = this.refs.cartItemRows?.[line - 1];
    if (!lineItemRow) return;

    const bundleId = lineItemRow.dataset.bundleId;
    const isAddon = lineItemRow.dataset.isAddon === 'true';

    // If changing quantity of the parent saree, also sync the quantity of its add-ons
    if (!isAddon) {
      const norm = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
      const targetTitle = norm(lineItemRow.dataset.productTitle);
      const targetItemTitle = norm(lineItemRow.dataset.itemTitle);
      const updates = {};
      updates[lineItemRow.dataset.key] = quantity;

      const allRows = Array.from(this.querySelectorAll('tbody tr[data-key]'));
      allRows.forEach((r) => {
        if (r === lineItemRow || r.dataset.isAddon !== 'true') return;
        const rBundle = r.dataset.bundleId;
        const rForSaree = norm(r.dataset.forSaree);

        const matchesBundle = Boolean(bundleId && rBundle && bundleId === rBundle);
        const matchesTitle = Boolean(
          rForSaree && (
            rForSaree === targetTitle ||
            rForSaree === targetItemTitle ||
            (targetTitle && (targetTitle.includes(rForSaree) || rForSaree.includes(targetTitle))) ||
            (targetItemTitle && (targetItemTitle.includes(rForSaree) || rForSaree.includes(targetItemTitle)))
          )
        );

        if (matchesBundle || matchesTitle) {
          updates[r.dataset.key] = quantity;
        }
      });

      this.updateQuantity({
        line,
        quantity,
        action: 'change',
        multiUpdates: updates,
      });
      return;
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - Optional click event.
   */
  onLineItemRemove(line, event) {
    const targetRow = (event && event.target && event.target.closest('tr')) || this.refs.cartItemRows?.[line - 1];

    if (!targetRow) return;

    const bundleId = targetRow.dataset.bundleId;
    const isAddon = targetRow.dataset.isAddon === 'true';
    const norm = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const productTitle = norm(targetRow.dataset.productTitle);
    const itemTitle = norm(targetRow.dataset.itemTitle);

    const allRows = Array.from(this.querySelectorAll('tbody tr[data-key]'));
    const rowsToRemove = [targetRow];

    // Get all nested lines of the row to remove
    this.refs.cartItemRows?.forEach((row) => {
      if (row.dataset.parentKey === targetRow.dataset.key && !rowsToRemove.includes(row)) {
        rowsToRemove.push(row);
      }
    });

    // If removing the parent saree, automatically remove all associated add-on services as well
    if (!isAddon) {
      const remainingSarees = allRows.filter((r) => r !== targetRow && r.dataset.isAddon !== 'true');

      if (remainingSarees.length === 0) {
        // If no other saree remains in the cart, remove ALL add-ons
        allRows.forEach((row) => {
          if (row.dataset.isAddon === 'true' && !rowsToRemove.includes(row)) {
            rowsToRemove.push(row);
          }
        });
      } else {
        allRows.forEach((row) => {
          if (row === targetRow || row.dataset.isAddon !== 'true') return;
          const rBundle = row.dataset.bundleId;
          const rForSaree = norm(row.dataset.forSaree);

          const matchesBundle = Boolean(bundleId && rBundle && bundleId === rBundle);
          const matchesTitle = Boolean(
            rForSaree && (
              rForSaree === productTitle ||
              rForSaree === itemTitle ||
              (productTitle && (productTitle.includes(rForSaree) || rForSaree.includes(productTitle))) ||
              (itemTitle && (itemTitle.includes(rForSaree) || rForSaree.includes(itemTitle)))
            )
          );

          if ((matchesBundle || matchesTitle) && !rowsToRemove.includes(row)) {
            rowsToRemove.push(row);
          }
        });
      }
    }

    // Add class to the rows to trigger the removal animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      rowsToRemove,
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {Array<HTMLElement>} [config.rowsToRemove] - Rows to remove.
   * @param {Record<string, number>} [config.multiUpdates] - Multiple updates map.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, rowsToRemove, multiUpdates } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    let endpoint = Theme.routes.cart_change_url;
    let body;

    if (multiUpdates) {
      endpoint = '/cart/update.js';
      body = JSON.stringify({
        updates: multiUpdates,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });
    } else if (rowsToRemove && rowsToRemove.length > 0) {
      const updates = {};
      rowsToRemove.forEach((row) => {
        if (row.dataset.key) {
          updates[row.dataset.key] = 0;
        }
      });
      endpoint = '/cart/update.js';
      body = JSON.stringify({
        updates,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });
    } else {
      body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });
    }

    cartTotal?.shimmer();

    fetch(`${endpoint}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
